import { useCallback, useEffect, useState } from "react";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  addDays,
  addMinutes,
  addMonths,
  addYears,
  differenceInDays,
  isSameDay,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import axios from "axios";
import { useBookingContext } from "hooks/useBooking";
import { DateObject } from "react-multi-date-picker";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { styled } from "@mui/material/styles";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

// utils
import {
  AvailableSlotsNew as Slot,
  ServiceInfoType,
  ServiceType,
} from "utils/booking";

import Iconify from "../iconify";

import Range from "./range";
// =================================================================

const fetcher = async (
  url: string,
  params: any,
  controller?: AbortController,
) => {
  const res = await axios.post(url, params, {
    signal: controller?.signal,
  });
  return res.data;
};

const CalendarTextField = styled(TextField)({
  "& .MuiOutlinedInput-input": {
    fontFamily: "ABCDiaTypeRegular",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      borderColor: "yellow",
    },
  },
});

const isOverSeven = (start: Date, end: Date) => {
  const diff = differenceInDays(start, end) > 7;
  return diff;
};
const determinMin = (info: ServiceInfoType, services: ServiceType[]) => {
  const hasNullMax = services.find(
    (s) => s?.Minimum_Booking_Notice_Min === null,
  );

  if (
    services.length === 0 ||
    (hasNullMax && Number(info.Minimum_Booking_Notice_Min) > 0)
  )
    return Number(info.Minimum_Booking_Notice_Min) ?? 0;
  else if (hasNullMax) return 0;

  const maxService = services?.reduce((prev, current) => {
    return Number(prev?.Minimum_Booking_Notice_Min ?? 0) >
      Number(current?.Minimum_Booking_Notice_Min ?? 0)
      ? prev
      : current;
  });
  if (
    maxService &&
    Number(maxService.Minimum_Booking_Notice_Min) >
      Number(info.Minimum_Booking_Notice_Min)
  )
    return Number(maxService.Minimum_Booking_Notice_Min);
  return Number(info.Minimum_Booking_Notice_Min);
};

const determineMax = (info: ServiceInfoType, services: ServiceType[]) => {
  const hasNullMax = services.find(
    (s) => s?.Maximum_Booking_Notice_Min === null,
  );

  if (
    services.length === 0 ||
    (hasNullMax && Number(info.Maximum_Booking_Notice_Min) > 0)
  )
    return Number(info.Maximum_Booking_Notice_Min) ?? 0;
  else if (hasNullMax) return 0;

  const maxService = services?.reduce((prev, current) => {
    return Number(prev?.Maximum_Booking_Notice_Min ?? 0) >
      Number(current?.Maximum_Booking_Notice_Min ?? 0)
      ? prev
      : current;
  });
  if (
    maxService &&
    Number(maxService.Maximum_Booking_Notice_Min) >
      Number(info.Maximum_Booking_Notice_Min)
  )
    return Number(maxService.Maximum_Booking_Notice_Min);
  return Number(info.Maximum_Booking_Notice_Min);
};

const RangeSelector = ({
  onRangeChanged,
  dpc,
  staff,
  timeZone,
  useAll,
  info,
  serviceId,
  startDate,
}: {
  staff: ServiceType[];
  // eslint-disable-next-line no-unused-vars
  onRangeChanged: (param: {
    slot: Slot[];
    isLoading: boolean;
    selectedDay: string;
  }) => void;
  timeZone: string;
  dpc: ServiceType | null;
  useAll: boolean;
  info: ServiceInfoType;
  serviceId: string;
  startDate: Date;
}) => {
  const context = useBookingContext();
  context.data?.services;
  const [open, setOpen] = useState(false);
  const [rawSlots, setRawSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [maxDate, setmaxDate] = useState<string | null>(null);

  const [range, setRange] = useState<{
    start: string;
    end: string;
    auto: boolean;
    retries: number;
  }>({
    start: formatInTimeZone(
      new Date(startOfDay(startDate)) ?? new Date(),
      timeZone,
      "yyyy-MM-dd 00:00:00XXX",
    ),
    end: formatInTimeZone(
      addDays(new Date(), 7),
      timeZone,
      "yyyy-MM-dd HH:mm:ssXXX",
    ),
    auto: false,
    retries: 0,
  });

  const [selectedDay, setSelectedDay] = useState("");
  const [displayDate, setDisplayDate] = useState<string | undefined>(undefined);
  const fetchSlots = useCallback(
    async (controller: AbortController) => {
      try {
        const currentDate = parseISO(
          formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd 00:00:00XXX"),
        );

        if (
          (useAll && staff.length === 0) ||
          (!useAll && dpc === null) ||
          range.retries === 5
        ) {
          return;
        }
      if (useAll && Number(info?.Maximum_Booking_Notice_Min) === 0)
        setmaxDate(
          formatInTimeZone(
            addYears(new Date(), 1),
            timeZone,
            "yyyy-MM-dd HH:mm:ssXXX",
          ),
        );
      else {
        const max = determineMax(info, staff);

        setmaxDate(
          determineMax(info, staff) > 0
            ? formatInTimeZone(
                addMinutes(currentDate, max),
                timeZone,
                "yyyy-MM-dd HH:mm:ssXXX",
              )
            : formatInTimeZone(
                addYears(currentDate, 1),
                timeZone,
                "yyyy-MM-dd HH:mm:ssXXX",
              ),
        );
      }

      const getStartDate = (dpc: ServiceType) => {
        const minimum =
          dpc.Minimum_Booking_Notice_Min ??
          info.Minimum_Booking_Notice_Min ??
          0;

        if (
          isSameDay(
            parseISO(range.start),
            parseISO(
              formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd 00:00:ssXXX"),
            ),
          )
        ) {
          const dte = parseISO(
            formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd HH:00:ssXXX"),
          );

          return formatInTimeZone(
            addMinutes(dte, minimum),
            timeZone,
            "yyyy-MM-dd HH:00:ssXXX",
          );
        } else
          return formatInTimeZone(
            startOfDay(parseISO(range.start)),
            timeZone,
            "yyyy-MM-dd 00:00:ssXXX",
          );
      };

      const getEndDate = (dpc: ServiceType) => {
        const maximum =
          dpc.Maximum_Booking_Notice_Min ??
          info.Maximum_Booking_Notice_Min ??
          0;

        if (maximum === 0)
          return formatInTimeZone(
            addMonths(parseISO(range.start), 6),
            timeZone,
            "yyyy-MM-dd HH:00:ssXXX",
          );

        const dte = parseISO(
          formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd HH:00:ssXXX"),
        );

        if (isSameDay(parseISO(range.start), dte))
          return formatInTimeZone(
            addMinutes(dte, maximum),
            timeZone,
            "yyyy-MM-dd HH:00:ssXXX",
          );
        else
          return formatInTimeZone(
            addMinutes(parseISO(range.start), maximum),
            timeZone,
            "yyyy-MM-dd HH:00:ssXXX",
          );
      };

      const generatePayload = (dpc: ServiceType) => {
        const start = getStartDate(source[0]);
        const end = isOverSeven(
          parseISO(getEndDate(source[0])),
          parseISO(
            formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd 23:59:ssXXX"),
          ),
        )
          ? formatInTimeZone(range.end, timeZone, "yyyy-MM-dd 23:59:ssXXX")
          : formatInTimeZone(
              getEndDate(source[0]),
              timeZone,
              "yyyy-MM-dd 23:59:ssXXX",
            );
        const payload = {
          staff: [
            {
              id: dpc?.staff_id,
              blockedDays: dpc?.blockedDays ?? [],
              email: dpc?.staff_email,
            },
          ],

          interval: info.Duration_Min,
          buffer: info.Buffer_Time_Min,
          startDate: start,
          timeZone,
          endDate: end,
        };
        return payload;
      };
      const shuffle = (array: ServiceType[]) => {
        for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
      };
      const source = useAll ? shuffle(staff) : [dpc!];

      let data: any = [];

      setLoading(true);

        const allResults = await Promise.all(
          source.map((p) =>
            fetcher(
              `${process.env.TL_API}/calendar`,
              generatePayload(p),
              controller,
            ),
          ),
        );

        allResults.forEach((result) => {
          data.push(...result);
        });
        setLoading(false);
        setSelectedDay(formatInTimeZone(range.start, timeZone, "yyyy-MM-dd"));

        const filtered = data.filter(
          (d: Slot) =>
            formatInTimeZone(d.date, timeZone, "yyyy-MM-dd") <=
            formatInTimeZone(range.end, timeZone, "yyyy-MM-dd"),
        );
        setRawSlots(filtered ?? []);
      } catch (error: any) {
        // Silently ignore cancellation errors - these are expected when the component
        // unmounts or dependencies change before the request completes
        if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
          return;
        }

        throw error;
      }
    },
    [timeZone, useAll, staff, dpc, range.retries, range.start, range.end, info],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchSlots(controller);
    return () => {
      controller.abort();
    };
  }, [fetchSlots]);

  useEffect(() => {
    if (rawSlots?.length > 0 && !loading) {
      let selected =
        selectedDay ?? formatInTimeZone(range.start, timeZone, "yyyy-MM-dd");

      const slotsForTheDay = rawSlots.filter((t) => t.date === selected);
      const emptySlots = slotsForTheDay.length === 0;

      if (emptySlots) setSelectedDay(rawSlots[0].date);

      onRangeChanged({
        slot: !emptySlots ? slotsForTheDay : rawSlots,
        isLoading: loading,
        selectedDay: !emptySlots ? selected : rawSlots[0].date,
      });
    } else if (rawSlots?.length === 0 && !loading && range.retries < 5) {
      setLoading(true);
      const nextEnd = addDays(parseISO(range.end), 7);
      setRange((prev) => ({
        start: formatInTimeZone(
          addDays(parseISO(range.end), 1),
          timeZone,
          "yyyy-MM-dd HH:mm:ssXXX",
        ),
        end: formatInTimeZone(nextEnd, timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
        auto: false,
        retries: prev.retries + 1,
      }));
    } else if (range.retries === 5) {
      setLoading(false);
      onRangeChanged({ slot: [], isLoading: false, selectedDay: "" });
    } else {
      onRangeChanged({
        slot: [],
        isLoading: loading,
        selectedDay: "",
      });
    }
  }, [
    loading,
    onRangeChanged,
    range,
    rawSlots,
    selectedDay,
    timeZone,
    info,
    staff,
  ]);

  const handlePrevious = () => {
    let nextEnd = formatInTimeZone(
      addDays(parseISO(range.end), -7),
      timeZone,
      "yyyy-MM-dd HH:mm:ssXXX",
    );
    let nextStart = formatInTimeZone(
      addDays(parseISO(range.end), -13),
      timeZone,
      "yyyy-MM-dd HH:mm:ssXXX",
    );
    const currentDate = parseISO(
      formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
    );

    if (parseISO(nextStart) < currentDate) {
      nextStart = formatInTimeZone(
        currentDate,
        timeZone,
        "yyyy-MM-dd HH:mm:ssXXX",
      );
      nextEnd = formatInTimeZone(
        addDays(currentDate, 7),
        timeZone,
        "yyyy-MM-dd HH:mm:ssXXX",
      );
    }

    setRawSlots([]);
    setLoading(true);
    setRange({
      start: nextStart,
      end: nextEnd,
      auto: false, //nextEnd.getMonth() < range.start.getMonth(),
      retries: 0,
    });
    onRangeChanged({
      slot: [],
      isLoading: true,
      selectedDay: "",
    });
  };

  const handleNext = () => {
    const nextEnd = formatInTimeZone(
      addDays(parseISO(range.end), 7),
      timeZone,
      "yyyy-MM-dd HH:mm:ssXXX",
    );
    setRawSlots([]);
    setRange({
      start: formatInTimeZone(
        addDays(parseISO(range.end), 1),
        timeZone,
        "yyyy-MM-dd HH:mm:ssXXX",
      ),
      end: nextEnd,
      auto: false, //nextEnd.getMonth() > range.start.getMonth(),
      retries: 0,
    });
    setLoading(true);
    onRangeChanged({
      slot: [],
      isLoading: true,
      selectedDay: "",
    });
  };

  const handleMonthChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    // onDaySelected('');
    const currentDate = parseISO(
      formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
    );

    setRawSlots([]);
    if (date < currentDate) {
      setDisplayDate(formatInTimeZone(currentDate, timeZone, "yyyy-MM-dd"));
      setRange({
        start: formatInTimeZone(
          addDays(currentDate, 3),
          timeZone,
          "yyyy-MM-dd HH:mm:ssXXX",
        ),
        end: formatInTimeZone(
          addDays(currentDate, 7),
          timeZone,
          "yyyy-MM-dd HH:mm:ssXXX",
        ),
        auto: false,
        retries: 0,
      });
    } else {
      const first = startOfMonth(date);
      setDisplayDate(
        formatInTimeZone(first, timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
      );
      setRange({
        start: formatInTimeZone(
          addDays(first, -7),
          timeZone,
          "yyyy-MM-dd HH:mm:ssXXX",
        ),
        end: formatInTimeZone(first, timeZone, "yyyy-MM-dd HH:mm:ssXXX"),
        auto: false,
        retries: 0,
      });
    }
  };

  const handleDateSelected = (date: string) => {
    setSelectedDay(date);
    setDisplayDate(formatInTimeZone(date, timeZone, "yyyy-MM-dd"));
  };

  const disabledNext = () => {
    return (
      maxDate !== null && parseISO(maxDate) < addDays(parseISO(range.end), 1)
    );
  };

  return (
    <>
      <Box sx={{ mt: 4, width: "100%" }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <DatePicker
              views={["year", "month"]}
              label={selectedDay !== "" ? "Selected Month and Year" : ""}
              minDate={new Date(new DateObject().toFirstOfMonth().format())}
              maxDate={maxDate ? parseISO(maxDate) : addYears(new Date(), 1)}
              value={displayDate ? parseISO(displayDate) : selectedDay}
              onChange={handleMonthChange}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              renderInput={(params) => (
                <Stack
                  direction={"row"}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <CalendarTextField
                    {...params}
                    ref={params.inputRef}
                    helperText={null}
                    size="small"
                    fullWidth={false}
                    sx={{
                      "& .MuiOutlinedInput-input": {
                        fontFamily: "ABCDiaTypeRegular",
                        width: { xs: "100%", sm: "60%" },
                        fontSize: "20px",
                      },
                    }}
                    variant="outlined"
                  />
                  <IconButton onClick={() => setOpen(true)} sx={{ m: 0, p: 0 }}>
                    <Iconify
                      icon="ant-design:caret-down-filled"
                      color="primary"
                      width={24}
                      height={24}
                      sx={{ display: { xs: "block", sm: "none" } }}
                    />
                  </IconButton>
                </Stack>
              )}
            />
            <Button
              variant="text"
              sx={{
                fontFamily: "ABCDiaTypeRegular",
                textTransform: "none",
                textDecoration: "underline",
                fontSize: "16px",
                width: "70%",
              }}
              onClick={() => setOpen(true)}
            >
              See More
            </Button>
          </Box>
        </LocalizationProvider>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!loading && (
          <Range
            range={{ ...range, isLoading: loading }}
            rawSlots={rawSlots}
            selectedDay={selectedDay}
            handleDateSelected={handleDateSelected}
            timeZone={timeZone}
            maxDate={maxDate}
            minimum={determinMin(info, staff)}
            serviceId={serviceId}
          />
        )}
      </Box>
      <Box
        sx={{
          width: "97%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          my: 1,
        }}
      >
        <IconButton
          onClick={handlePrevious}
          disabled={new Date(range.start) < addDays(new Date(), 1)}
        >
          <Iconify icon="akar-icons:chevron-left" color="primary" />
        </IconButton>
        <IconButton onClick={handleNext} disabled={disabledNext()}>
          <Iconify icon="akar-icons:chevron-right" color="primary" />
        </IconButton>
      </Box>
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
            <Typography
              align={"center"}
              variant="caption"
              component="div"
              color={"primary"}
            >
              Please wait...
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default RangeSelector;
